/** Null-safe labels and product-copy helpers for Hype Radar V2 UI. */

import { ADDRESS_EXPLORERS, shortAddress } from "@/lib/format";

export function fmtPct01(v) {
  if (v == null || Number.isNaN(Number(v))) return null;
  return Number(v).toFixed(2);
}

export function fmtScore(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return Number(v).toFixed(1);
}

export function fmtPoints(v) {
  if (v == null || Number.isNaN(Number(v))) return "0.0";
  return Number(v).toFixed(1);
}

export function displayOr(value, empty) {
  if (value == null || value === "" || value === "—") return empty;
  return value;
}

/** Compact USD for timeline details — only when a real number exists. */
export function fmtCompactUsd(v) {
  if (v == null || Number.isNaN(Number(v))) return null;
  const n = Number(v);
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}

/** Product-facing status labels (backend enums stay unchanged). */
const IDENTITY_COPY = {
  NARRATIVE_ONLY: "Narrative Only",
  ASSET_CANDIDATE: "Asset Candidate",
  ENTITY_RESOLVED: "Resolved Asset",
  ENTITY_LINKED: "Entity Linked",
  NO_NARRATIVE_SIGNALS: "No Active Signals",
  RESOLVED: "Resolved Asset",
  UNRESOLVED: "Unresolved",
};

const OPPORTUNITY_COPY = {
  NO_NARRATIVE_SIGNALS: "Insufficient Data",
  INSUFFICIENT_DATA: "Insufficient Data",
  STALE: "Stale Signal",
  EARLY: "Early",
  ACCELERATING: "Accelerating",
  MATURE: "Mature",
  WATCH: "Watch",
};

/** Asset-resolution enums must not be shown as Narrative Intelligence. */
const ASSET_RESOLUTION_STATUSES = new Set([
  "ASSET_CANDIDATE",
  "ENTITY_RESOLVED",
  "ENTITY_LINKED",
  "RESOLVED",
  "UNRESOLVED",
]);

export function identityLabel(status) {
  return IDENTITY_COPY[status] || status || "—";
}

export function assetIdentityLabel(assetIdentity, tokenFallback = null) {
  if (assetIdentity?.status) return identityLabel(assetIdentity.status);
  if (assetIdentity?.label) return assetIdentity.label;
  // Defense-in-depth: structural resolved token must not render "—".
  if (
    tokenFallback &&
    tokenFallback.entity_id &&
    tokenFallback.chain &&
    (tokenFallback.contract_address || tokenFallback.mint_address)
  ) {
    return "Resolved Asset";
  }
  return assetIdentity ? "—" : "—";
}

/**
 * Token drawer: narrative intelligence label from status object.
 * Does not treat ASSET_CANDIDATE as a narrative-intelligence state.
 */
export function narrativeStatusLabel(ni, tokenFallback = null) {
  if (ni) {
    const status = ni.status;
    if (status === "NO_NARRATIVE_SIGNALS") return "No Active Signals";
    if (status === "NARRATIVE_ONLY") return "Active Signal";
    if (ASSET_RESOLUTION_STATUSES.has(status)) {
      if (ni.has_intelligence === true) return "Active Signal";
      return "No Active Signals";
    }
    if (ni.has_intelligence === false) return "No Active Signals";
    if (ni.label === "No Narrative Signals") return "No Active Signals";
    if (ni.has_intelligence === true) return "Active Signal";
    if (ni.label) return ni.label;
  }
  // Compatibility: zero sources / no momentum → No Active Signals
  if (tokenFallback) {
    const ei = tokenFallback.external_intelligence || {};
    const sources =
      ei.source_count ?? ei.providers?.length ?? ei.experimental?.provider_count ?? 0;
    const mom = ei.narrative_momentum ?? ei.experimental?.narrative_momentum;
    if (Number(sources) === 0 && (mom == null || Number(mom) === 0)) {
      return "No Active Signals";
    }
  }
  return ni ? "—" : "—";
}

/**
 * Narrative drawer: signal presence only (not asset resolution).
 * Active Signal | No Active Signals | Stale Signal
 */
export function narrativeSignalLabel(data) {
  if (!data) return "No Active Signals";
  const opp = data.opportunity || {};
  if (opp.status === "STALE" || opp.ui_label === "STALE" || data.staleness === "stale" || data.staleness === "expired") {
    const hasSignal =
      Number(data.narrative_momentum ?? data.momentum) > 0 ||
      Number(data.source_count ?? data.provider_count) > 0 ||
      Number(data.observation_count) > 0 ||
      data.narrative_intelligence?.has_intelligence === true;
    if (hasSignal) return "Stale Signal";
  }
  const ni = data.narrative_intelligence_status || data.narrative_intelligence || {};
  if (ni.status === "NO_NARRATIVE_SIGNALS" || ni.has_intelligence === false) {
    return "No Active Signals";
  }
  const mom = Number(data.narrative_momentum ?? data.momentum);
  const sources = Number(data.source_count ?? data.provider_count ?? 0);
  const obs = Number(data.observation_count ?? 0);
  if (ni.has_intelligence === true || (Number.isFinite(mom) && mom > 0) || sources > 0 || obs > 0) {
    return "Active Signal";
  }
  if (ASSET_RESOLUTION_STATUSES.has(ni.status) || ni.status === "NARRATIVE_ONLY") {
    // NARRATIVE_ONLY / candidate-bearing rows imply an active narrative topic.
    return "Active Signal";
  }
  return "No Active Signals";
}

/**
 * Narrative drawer: asset resolution from candidates / related entities.
 * No Asset Match | Asset Candidate | Entity Linked | Resolved Asset
 */
export function assetResolutionLabel(data) {
  if (!data) return "No Asset Match";
  const candidates = data.asset_candidates || [];
  const related = data.related_entities || [];
  const statuses = candidates.map((c) => c.status || c.candidate_status);

  if (
    statuses.includes("ENTITY_RESOLVED") ||
    statuses.includes("ENTITY_LINKED") ||
    related.length > 0
  ) {
    if (statuses.includes("ENTITY_RESOLVED") || related.length > 0) return "Resolved Asset";
    return "Entity Linked";
  }
  if (candidates.length > 0 || statuses.includes("ASSET_CANDIDATE")) {
    return "Asset Candidate";
  }
  const ni = data.narrative_intelligence || {};
  if (ni.status === "ASSET_CANDIDATE") return "Asset Candidate";
  if (ni.status === "ENTITY_LINKED") return "Entity Linked";
  if (ni.status === "ENTITY_RESOLVED") return "Resolved Asset";
  return "No Asset Match";
}

export function opportunityLabel(opp, data = null) {
  if (!opp && !data) return null;

  // Compatibility guard only: backend must also refuse STALE without intel.
  // If a payload claims STALE while there are zero sources, zero observations,
  // and no narrative momentum, display Insufficient Data instead.
  if (data && (opp?.status === "STALE" || opp?.ui_label === "STALE")) {
    const ei = data.external_intelligence || {};
    const sources = Number(
      ei.source_count ?? ei.providers?.length ?? ei.experimental?.provider_count ?? data.source_count ?? 0,
    );
    const obs = Number(data.observation_count ?? ei.signals?.length ?? 0);
    const mom = Number(
      data.narrative_momentum ?? data.momentum ?? ei.narrative_momentum ?? ei.experimental?.narrative_momentum,
    );
    const noIntel =
      sources <= 0 &&
      obs <= 0 &&
      (!Number.isFinite(mom) || mom <= 0) &&
      !(ei.signals && ei.signals.length);
    if (noIntel) {
      return "Insufficient Data";
    }
  }

  if (!opp) return null;
  if (opp.reason === "no_narrative_signals" || opp.ui_label === "NO_NARRATIVE_SIGNALS") {
    return "Insufficient Data";
  }
  const key = opp.ui_label || opp.status;
  return OPPORTUNITY_COPY[key] || key || null;
}

/** Deterministic helper under Opportunity when INSUFFICIENT_DATA. */
export function opportunityExplanation(data) {
  const opp = data?.opportunity;
  if (!opp) return null;
  const insufficient =
    opp.status === "INSUFFICIENT_DATA" ||
    opp.ui_label === "INSUFFICIENT_DATA" ||
    opp.ui_label === "NO_NARRATIVE_SIGNALS" ||
    opp.reason === "no_narrative_signals" ||
    opp.reason === "insufficient_components";
  if (!insufficient) return null;

  const mom = Number(data.narrative_momentum ?? data.momentum ?? data.external_intelligence?.narrative_momentum);
  const sources = Number(
    data.source_count ??
      data.provider_count ??
      data.external_intelligence?.source_count ??
      0,
  );
  const conf = Number(
    data.signal_confidence ??
      data.provider_consensus ??
      data.consensus_score ??
      data.external_intelligence?.signal_confidence,
  );
  const strongNarrative =
    (Number.isFinite(mom) && mom >= 0.4) ||
    (Number.isFinite(conf) && conf >= 0.5 && sources >= 1) ||
    sources >= 1;

  if (strongNarrative) {
    return "Strong narrative evidence, but not enough market or conviction data to classify an opportunity stage.";
  }
  return "Not enough signal evidence to classify an opportunity stage.";
}

/**
 * Read-time first/last seen normalization (defense in depth).
 * Returns { first, last } ISO strings or nulls with first <= last when both parse.
 */
export function normalizeSeenWindow(firstDetectedAt, lastSeenAt) {
  const parse = (iso) => {
    if (!iso) return null;
    const t = Date.parse(iso);
    return Number.isFinite(t) ? t : null;
  };
  const a = parse(firstDetectedAt);
  const b = parse(lastSeenAt);
  if (a == null && b == null) return { first: firstDetectedAt || null, last: lastSeenAt || null };
  if (a == null) return { first: lastSeenAt, last: lastSeenAt };
  if (b == null) return { first: firstDetectedAt, last: firstDetectedAt };
  if (a <= b) return { first: firstDetectedAt, last: lastSeenAt };
  return { first: lastSeenAt, last: firstDetectedAt };
}

export function resolutionMethodLabel(method, { audience = "public" } = {}) {
  if (!method) return null;
  if (method === "trusted_canonical_alias") return "Canonical Match";
  if (method === "chain_contract_exact") return "Exact Contract Match";
  if (audience === "internal") return method;
  if (method.includes("_")) return "Resolved";
  return method;
}

export function resolutionConfidenceLabel(candidate) {
  if (!candidate) return null;
  if (candidate.resolution_method === "trusted_canonical_alias") return "Trusted";
  if (candidate.resolution_method === "chain_contract_exact") return "Exact";
  if (candidate.status === "ENTITY_RESOLVED" || candidate.status === "ENTITY_LINKED") return "Linked";
  return null;
}

export function chainDisplayName(chain) {
  if (!chain) return "Unknown";
  const map = {
    bitcoin: "Bitcoin",
    ethereum: "Ethereum",
    solana: "Solana",
    base: "Base",
    bnb: "BNB",
    arbitrum: "Arbitrum",
    optimism: "Optimism",
    polygon: "Polygon",
    avalanche: "Avalanche",
    robinhood: "Robinhood Chain",
    hyperliquid: "Hyperliquid",
  };
  return map[String(chain).toLowerCase()] || chain;
}

export function candidateKindLabel(candidate) {
  if (!candidate) return null;
  if (candidate.asset_kind === "NATIVE_ASSET") return "Native Asset";
  if (candidate.asset_kind === "CONTRACT_ASSET") return "Contract Asset";
  return null;
}

export function candidateStatusLine(candidate) {
  if (!candidate) return "";
  const kind = candidateKindLabel(candidate);
  const status = identityLabel(candidate.status || candidate.candidate_status);
  if (kind === "Native Asset" && status === "Asset Candidate") return "Native Asset Candidate";
  return status || kind || "";
}

export function isLinkedCandidate(candidate) {
  const s = candidate?.status || candidate?.candidate_status;
  return s === "ENTITY_RESOLVED" || s === "ENTITY_LINKED";
}

/** Deterministic "What's happening?" blurb — no LLM, no investment claims. */
export function buildTokenSummary(data) {
  if (!data) return "";
  const comps = data.components || {};
  const mv = Number(comps.market_velocity);
  const bv = Number(comps.backing_velocity);
  const ca = Number(comps.caller_activity);
  const ni = data.narrative_intelligence_status || data.narrative_intelligence || {};
  const hasNarr =
    ni.has_intelligence === true ||
    (ni.status && ni.status !== "NO_NARRATIVE_SIGNALS" && !ASSET_RESOLUTION_STATUSES.has(ni.status)) ||
    (ASSET_RESOLUTION_STATUSES.has(ni.status) && ni.has_intelligence === true);
  const ei = data.external_intelligence || {};
  const mom = Number(ei.narrative_momentum ?? ei.experimental?.narrative_momentum);

  if (!hasNarr && Number.isFinite(mv) && mv >= 0.5 && (!(bv > 0.05)) && (!(ca > 0.05))) {
    return "Strong market activity, but no narrative, backing, or caller signals are currently confirmed.";
  }
  if (!hasNarr) {
    return "No active narrative signals are currently confirmed for this asset.";
  }
  if (Number.isFinite(mom) && mom >= 0.4) {
    return "Elevated narrative momentum is present alongside current market metrics.";
  }
  return "Narrative and market signals are available for review below.";
}

export function buildNarrativeSummary(data) {
  if (!data) return "";
  const title = String(data.title || "");
  const mom = Number(data.narrative_momentum ?? data.momentum);
  const candidates = data.asset_candidates || [];
  const native = candidates.find(
    (c) => c.asset_kind === "NATIVE_ASSET" || c.resolution_method === "trusted_canonical_alias",
  );
  const linked = candidates.some(isLinkedCandidate) || (data.related_entities || []).length > 0;
  const name =
    native?.name ||
    (/\bbitcoin\b/i.test(title) ? "Bitcoin" : null) ||
    (/\bethereum\b/i.test(title) ? "Ethereum" : null) ||
    (/\bsolana\b/i.test(title) ? "Solana" : null);

  const rangeMatch = title.match(/\$[\d,]+(?:\s*[–—-]\s*\$?[\d,]+)?/);
  const range = rangeMatch ? rangeMatch[0].replace(/\s+/g, " ").trim() : null;
  const sym = native?.symbol || (name === "Bitcoin" ? "BTC" : name === "Ethereum" ? "ETH" : name === "Solana" ? "SOL" : null);

  if (name && Number.isFinite(mom) && mom >= 0.4) {
    let s = `${name} is showing elevated narrative momentum`;
    if (range) s += ` around the ${range} range`;
    if (native) {
      s += ` and has been matched to the canonical ${sym || native.symbol || name} native asset`;
      if (linked) {
        s += `. It is linked to the resolved ${name} entity`;
      } else {
        s += `. It is not yet linked to a BackOnFlip entity`;
      }
    }
    return `${s}.`;
  }
  if (native) {
    const spaced = native.symbol ? ` (${native.symbol})` : "";
    if (linked) {
      return `Matched to canonical native asset ${native.name || "asset"}${spaced} and linked to a BackOnFlip entity.`;
    }
    return `Matched to canonical native asset ${native.name || "asset"}${spaced}. It is not yet linked to a BackOnFlip entity.`;
  }
  if (Number.isFinite(mom)) {
    return "Narrative momentum and source signals are available below.";
  }
  return "Narrative details and related asset candidates are listed below.";
}

function _normText(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Product-friendly timeline presentation. Never renders identical title/detail. */
export function formatTimelineEvent(event) {
  if (!event) return { title: "Event", detail: null, meta: null, confirm: null };
  const type = event.event_type || "";
  let raw = String(event.description || "");
  let title = "Activity";
  let detail = raw || null;

  if (type === "narrative_first_detected" || /narrative first detected/i.test(raw)) {
    title = "Narrative detected";
    detail = raw
      .replace(/^Narrative first detected:\s*/i, "")
      .replace(/\s*·\s*\d+\s*confirming observations?\.?$/i, "")
      .trim() || null;
  } else if (
    type === "market_snapshot" ||
    type === "market_metrics" ||
    /market (metrics|activity) snapshot/i.test(raw)
  ) {
    title = "Market activity snapshot";
    const parts = [];
    const vol = event.volume_24h_usd ?? event.volume_usd;
    const liq = event.liquidity_usd;
    const volFmt = fmtCompactUsd(vol);
    const liqFmt = fmtCompactUsd(liq);
    if (volFmt) parts.push(`Volume ${volFmt}`);
    if (liqFmt) parts.push(`Liquidity ${liqFmt}`);
    if (parts.length) {
      detail = parts.join(" · ");
    } else {
      const cleaned = raw
        .replace(/market metrics snapshot/gi, "")
        .replace(/market activity snapshot/gi, "")
        .replace(/^[·\s]+|[·\s]+$/g, "")
        .trim();
      detail = cleaned || null;
    }
  } else if (raw) {
    title = raw.split("·")[0].trim() || type || "Activity";
    detail = raw.includes("·") ? raw.slice(raw.indexOf("·") + 1).trim() : null;
  } else if (type) {
    title = type.replace(/_/g, " ");
    detail = null;
  }

  if (detail && _normText(detail) === _normText(title)) {
    detail = null;
  }

  const n = Number(event.observation_count);
  const confirm =
    Number.isFinite(n) && n > 1 ? `Confirmed by ${n} observations` : null;

  return {
    title,
    detail,
    confirm,
    meta: event.source_type || type || null,
  };
}

export function tokenDetailKey(t) {
  if (!t) return "";
  if (t.entity_id) return t.entity_id;
  const chain = t.chain || "";
  const addr = t.contract_address || t.mint_address || "";
  if (chain && addr) return `${chain}:${addr}`;
  return addr || t.symbol || "";
}

export function narrativeDetailKey(n) {
  return n?.canonical_narrative_key || n?.narrative_key || n?.title || "";
}

/** Explorer URL from existing registry only — never invents hosts. */
export function explorerContractUrl(chain, address) {
  if (!address) return null;
  if (chain === "solana") {
    return `https://solscan.io/token/${address}`;
  }
  const base = ADDRESS_EXPLORERS[chain];
  return base ? `${base}${address}` : null;
}

export function abbreviateId(value) {
  return shortAddress(value) || value || "";
}

export function entityInternalPath(slug) {
  if (!slug) return null;
  return `/token/${slug}`;
}

/** Public-facing copy must never include third-party brands. */
export const PUBLIC_PROVIDER_LEAKS = ["aixbt", "nebula", "hiddensystems"];

export function textHasProviderLeak(text) {
  const blob = String(text || "").toLowerCase();
  return PUBLIC_PROVIDER_LEAKS.some((t) => blob.includes(t));
}
